import React, { useState, FormEvent } from 'react';
import { motion } from 'framer-motion';

interface Props {
  sectionVariants: any;
  itemVariants: any;
}

const ContactSection: React.FC<Props> = ({
  sectionVariants,
  itemVariants
}) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    website: '' // honeypot field
  });

  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  
  // Validation function
  const validateForm = () => {
    const errors: {[key: string]: string} = {};
    
    if (formData.website) {
      return {}; // Return empty errors to let the form "succeed" but we'll skip actual submission
    }

    // Name validation - prevent script injection
    if (!formData.name.trim()) {
      errors.name = "Name is required";
    } else if (formData.name.length > 100) {
      errors.name = "Name is too long";
    } else if (/[<>]/.test(formData.name)) {
      errors.name = "Name contains invalid characters";
    }

    // Email validation
    if (!formData.email) {
      errors.email = "Email is required";
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      errors.email = "Invalid email address";
    }

    // Message validation - prevent script injection
    if (!formData.message.trim()) {
      errors.message = "Message is required";
    } else if (formData.message.length > 1000) {
      errors.message = "Message is too long (1000 character limit)";
    } else if (/[<>]/.test(formData.message)) {
      errors.message = "Message contains invalid characters";
    }
    
    return errors;
  };
  
  // Form handler
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const errors = validateForm();
    setFormErrors(errors);
    
    // If form is valid, proceed with submission
    if (Object.keys(errors).length === 0) {
      setIsSubmitting(true);
      
      // Skip actual submission if honeypot is filled
      if (formData.website) {
        // Simulate success but don't actually submit
        setTimeout(() => {
          setFormData({ name: '', email: '', message: '', website: '' });
          setSubmitSuccess(true);
          setTimeout(() => setSubmitSuccess(false), 5000);
          setIsSubmitting(false);
        }, 1000);
        return;
      }
    
      try {
        // Form submission logic
        const response = await fetch('https://tndlabs-service-959217788172.us-central1.run.app/api/contact', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: formData.name.trim(),
            email: formData.email.trim(),
            message: formData.message.trim(),
            timestamp: new Date().toISOString(),
            source: window.location.hostname
          })
        });

        const data = await response.json();

        if (response.ok) {
          // Reset form on success
          setFormData({ name: '', email: '', message: '', website: '' });
          setSubmitSuccess(true);
          // Hide success message after 5 seconds
          setTimeout(() => setSubmitSuccess(false), 5000);
        } else {
          // Handle server validation errors
          if (data.errors) {
            setFormErrors(data.errors);
          } else {
            throw new Error(data.message || 'Form submission failed');
          }
        }
      } catch (error) {
        console.error('Error submitting form:', error);
        setFormErrors({form: 'Failed to send your message. Please try again later.'});
      } finally {
        setIsSubmitting(false);
      }
    }
  };
  
  // Input change handler
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  return (
    <motion.section
      id="contact"
      className="py-28 md:py-36 px-6 md:px-12 bg-white border-t border-ink-line"
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.1 }}
      variants={sectionVariants}
    >
      <div className="max-w-2xl mx-auto">
        <motion.div className="mb-14" variants={itemVariants}>
          <p className="text-xs uppercase tracking-eyebrow text-ink-muted mb-8">Contact</p>
          <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tight mb-6">
            Let's <span className="italic">talk.</span>
          </h2>
          <p className="text-lg text-ink-soft leading-relaxed">
            Reach out about a venture, a partnership, or anything else on your mind.
          </p>
        </motion.div>
        {submitSuccess && (
          <motion.div
            className="border border-ink-line bg-neutral-lightest text-ink p-4 mb-8"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
          >
            Thank you! Your message has been sent successfully.
          </motion.div>
        )}

        {formErrors.form && (
          <motion.div
            className="border border-red-300 bg-red-50 text-red-700 p-4 mb-8"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {formErrors.form}
          </motion.div>
        )}
        
        {/* Form content */}
        <form 
            className="flex flex-col gap-8" 
            onSubmit={handleSubmit} 
            noValidate
            aria-labelledby="contact-form-heading"
        >
            <h3 id="contact-form-heading" className="sr-only">Contact Form</h3>
            
            <div className="form-group">
            <label htmlFor="name" className="text-[11px] uppercase tracking-eyebrow text-ink-muted mb-2 block">Your Name</label>
            <input
                type="text"
                id="name"
                placeholder="Your name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                aria-required="true"
                aria-invalid={formErrors.name ? "true" : "false"}
                aria-describedby={formErrors.name ? "name-error" : undefined}
                autoComplete="name"
                className={`w-full border-0 border-b ${formErrors.name ? 'border-red-500' : 'border-ink-line'} bg-transparent px-0 py-4 text-ink placeholder-ink-muted focus:border-ink focus:outline-none focus:ring-0 transition-colors`}
            />
            {formErrors.name && <p id="name-error" className="text-red-600 text-sm mt-2" role="alert">{formErrors.name}</p>}
            </div>
            
            <div className="form-group">
            <label htmlFor="email" className="text-[11px] uppercase tracking-eyebrow text-ink-muted mb-2 block">Email Address</label>
            <input
                type="email"
                id="email"
                placeholder="Email address"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                aria-required="true"
                aria-invalid={formErrors.email ? "true" : "false"}
                aria-describedby={formErrors.email ? "email-error" : undefined}
                autoComplete="email"
                className={`w-full border-0 border-b ${formErrors.email ? 'border-red-500' : 'border-ink-line'} bg-transparent px-0 py-4 text-ink placeholder-ink-muted focus:border-ink focus:outline-none focus:ring-0 transition-colors`}
            />
            {formErrors.email && <p id="email-error" className="text-red-600 text-sm mt-2" role="alert">{formErrors.email}</p>}
            </div>
            
            <div className="form-group">
            <label htmlFor="message" className="text-[11px] uppercase tracking-eyebrow text-ink-muted mb-2 block">Message</label>
            <textarea
                id="message"
                placeholder="Your message"
                name="message"
                value={formData.message}
                onChange={handleInputChange}
                rows={4}
                required
                aria-required="true"
                aria-invalid={formErrors.message ? "true" : "false"}
                aria-describedby={formErrors.message ? "message-error" : undefined}
                className={`w-full border-0 border-b ${formErrors.message ? 'border-red-500' : 'border-ink-line'} bg-transparent px-0 py-4 text-ink placeholder-ink-muted focus:border-ink focus:outline-none focus:ring-0 transition-colors`}
            ></textarea>
            {formErrors.message && <p id="message-error" className="text-red-600 text-sm mt-2" role="alert">{formErrors.message}</p>}
            </div>
            
            {/* Honeypot field to catch bots - hidden from real users */}
            <div className="hidden" aria-hidden="true">
            <label htmlFor="website">Website</label>
            <input 
                type="text" 
                id="website" 
                name="website" 
                value={formData.website} // Bind value to state for honeypot check
                onChange={handleInputChange} // Use standard handler
                tabIndex={-1} 
                autoComplete="off"
            />
            </div>
            
            <div className="mt-6">
            <motion.button
                type="submit"
                disabled={isSubmitting}
                className={`px-8 py-4 bg-ink text-white text-sm font-medium tracking-wide hover:bg-ink-soft transition-colors flex items-center gap-3 ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                whileHover={!isSubmitting ? { x: 5 } : {}}
                whileTap={!isSubmitting ? { x: 0 } : {}}
                aria-live="polite"
            >
                {isSubmitting ? 'Sending...' : 'Send message'}
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M5 12H19M19 12L12 5M19 12L12 19" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </motion.button>
            </div>
        </form>
      </div>
    </motion.section>
  );
};

export default ContactSection;